import { Icon, IconButton, MenuItem } from "@material-ui/core";
import React from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
} from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { t } from "js/i18n";
import {
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog,
} from "js/react/components/Dialogs/BaseDialog";
import { DeleteWorkspaceResourceFolderDialog } from "js/react/components/Dialogs/DeleteWorkspaceResourceFolderDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { FlexSpacer } from "js/react/components/Gap";
import {
    FolderContainer,
    LibraryFolder,
    selectedClass,
    SideBarLabel,
    StyledAddIcon,
    StyledFoldersSection,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger();

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

const CREATE_FOLDER_CONFIG: Partial<Record<WorkspaceResourceType, { inputId: string; logTag: string }>> = {
    [WorkspaceResourceType.SLIDE]: {
        inputId: "create_slide_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
    [WorkspaceResourceType.SLIDE_TEMPLATE]: {
        inputId: "create_slide_template_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
    [WorkspaceResourceType.ASSET]: {
        inputId: "create_asset_folder_input",
        logTag: "WorkspaceResourceSideBarFolders",
    },
};

export type WorkspaceResourceFolderFilter = {
    folderId?: string | null;
    subFolderId?: string | null;
};

type WorkspaceResourceSideBarFolderProps = {
    folder: IWorkspaceResourceFolder;
    folderFilter?: WorkspaceResourceFolderFilter | null;
    onSetFilter?: (filter: WorkspaceResourceFolderFilter) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
};

function WorkspaceResourceSideBarFolder({
    folder,
    folderFilter,
    onSetFilter,
    readOnly = false,
    resourceTypes,
    resourceFolders,
    draggedItems,
    onFolderDrop,
}: WorkspaceResourceSideBarFolderProps) {
    const isSelected = folderFilter?.folderId === folder.id && !folderFilter?.subFolderId;

    const canRenameFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );
    const canDeleteFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToDeleteFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );

    const handleCanUpgradeAction = (cta: string) => () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta } });
        }
    };

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t("Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (resourceFolders.some(f => f.name === name)) {
                throw new Error(t("Please enter a unique folder name."));
            }

            await withProgressDialog({
                title: t("Renaming folder..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[WorkspaceResourceSideBarFolders] handleRenameFolder failed");

            ShowErrorDialog({
                title: t("Unable to rename folder"),
                message: (err as Error).message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, { folder });

        if (deleted && isSelected) {
            onSetFilter?.({ folderId: null, subFolderId: null });
        }
    };

    const handleMouseUp = async (event: React.MouseEvent | React.TouchEvent) => {
        if (draggedItems && draggedItems.length > 0) {
            event.stopPropagation();
            event.preventDefault();
            await onFolderDrop?.(folder);
            return;
        }

        onSetFilter?.({ folderId: folder.id, subFolderId: null });
    };

    return (
        <LibraryFolder
            data-attr={folder.name}
            className={selectedClass(isSelected)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon className="material-symbols-outlined">folder</Icon>
            <div className="folder-name">{folder.name}</div>
            <FlexSpacer />
            {!readOnly && !app.isConstrained &&
                <IconMenu>
                    {canRenameFolders &&
                        <MenuItem onClick={handleRenameFolder}>
                            <Icon>label</Icon>
                            {t("Rename Folder")}
                        </MenuItem>}
                    {canUpgradeToRenameFolders && (
                        <RestrictedMenuItem divider onClick={handleCanUpgradeAction("rename_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Rename Folder")}
                        </RestrictedMenuItem>
                    )}

                    <hr />
                    {canDeleteFolders &&
                        <MenuItem onClick={handleDeleteFolder}>
                            <Icon>delete</Icon>
                            {t("Delete Folder")}
                        </MenuItem>}

                    {canUpgradeToDeleteFolders &&
                        <RestrictedMenuItem onClick={handleCanUpgradeAction("delete_folder")}>
                            <UpgradeIcon style={{ marginRight: 8 }} />
                            {t("Delete Folder")}
                        </RestrictedMenuItem>}
                </IconMenu>
            }
        </LibraryFolder>
    );
}

export type WorkspaceResourceSideBarFoldersSectionProps = {
    folders: IWorkspaceResourceFolder[];
    folderFilter?: WorkspaceResourceFolderFilter | null;
    onSetFilter?: (filter: WorkspaceResourceFolderFilter) => void;
    readOnly?: boolean;
    resourceTypes: WorkspaceResourceType[];
    folderType?: WorkspaceResourceFolderType;
    createResourcesType?: WorkspaceResourceType;
    createFolderButtonId?: string;
    shareCreatedFoldersWithDefaultGroups?: boolean;
    draggedItems?: unknown[];
    onFolderDrop?: (folder: IWorkspaceResourceFolder) => void | Promise<void>;
};

export function WorkspaceResourceSideBarFoldersSection({
    folders,
    folderFilter,
    onSetFilter,
    readOnly = false,
    resourceTypes,
    folderType = WorkspaceResourceFolderType.SHARED,
    createResourcesType,
    createFolderButtonId,
    shareCreatedFoldersWithDefaultGroups = true,
    draggedItems,
    onFolderDrop,
}: WorkspaceResourceSideBarFoldersSectionProps) {
    const resourceFolders = WorkspaceController.resourceFolders;
    const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const resourcesTypeForCreate = createResourcesType ?? resourceTypes[0];
    const createConfig = resourcesTypeForCreate ? CREATE_FOLDER_CONFIG[resourcesTypeForCreate] : undefined;

    const handleCreateFolder = async () => {
        if (!canCreateFolders) {
            if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
                ShowDialog(ChangePlanDialog, {
                    featuredPlan: WorkspacePlanTier.TEAM,
                });
            } else {
                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You do not have permission to create folders!"),
                });
            }
            return;
        }

        const folderName = await new Promise<string | undefined>(resolve => ShowDialog(InputConfirmationDialog, {
            title: t("Create Folder"),
            input: {
                type: "text",
                placeholder: t("Type folder name..."),
                id: createConfig?.inputId,
                inputProps: {
                    maxLength: 50,
                },
            },
            acceptCallback: (input: string) => resolve(input.trim()),
        }));

        if (!folderName) {
            return;
        }

        if (resourceFolders
            .filter(resource => resourceTypes.includes(resource.resourcesType))
            .some(folder => folder.name === folderName)) {
            ShowWarningDialog({
                title: t("Unable to create folder"),
                message: t("Please enter a unique folder name."),
            });
            return;
        }

        if (!resourcesTypeForCreate) {
            return;
        }

        try {
            await withProgressDialog({
                title: t("Creating folder..."),
                action: () => {
                    if (shareCreatedFoldersWithDefaultGroups) {
                        return WorkspaceController.createWorkspaceResourceFolderAndShareWithDefaultGroups({
                            name: folderName,
                            resourcesType: resourcesTypeForCreate,
                            folderType
                        });
                    } else {
                        return WorkspaceController.createWorkspaceResourceFolder({
                            name: folderName,
                            resourcesType: resourcesTypeForCreate,
                            folderType
                        });
                    }
                }
            });
        } catch (err) {
            logger.error(err, `[${createConfig?.logTag}] handleCreateFolder failed`);

            ShowErrorDialog({
                title: t("Unable to create folder"),
                message: (err as Error).message,
            });
        }
    };

    return (
        <StyledFoldersSection>
            <SideBarLabel>
                <span>{t("Folders")}</span>
                {!readOnly && !app.isConstrained && canCreateFolders &&
                    <IconButton size="small" id={createFolderButtonId} onClick={handleCreateFolder}>
                        <StyledAddIcon>add</StyledAddIcon>
                    </IconButton>
                }
            </SideBarLabel>
            <FolderContainer>
                {folders.map(folder => (
                    <WorkspaceResourceSideBarFolder
                        key={folder.id}
                        folder={folder}
                        folderFilter={folderFilter}
                        onSetFilter={onSetFilter}
                        readOnly={readOnly}
                        resourceTypes={resourceTypes}
                        resourceFolders={resourceFolders.filter(f => f.resourcesType === resourcesTypeForCreate)}
                        draggedItems={draggedItems}
                        onFolderDrop={onFolderDrop}
                    />
                ))}
            </FolderContainer>
        </StyledFoldersSection>
    );
}
